import type { PropType } from 'vue';
import type { TSwitchProps } from './TSwitch.types';
import { merge } from 'es-toolkit/compat';
import { computed, defineComponent, ref, watch } from 'vue';
import { useStyles } from './TSwitch.styles';

const defaultProps = {
  labelPosition: 'right',
};

export const TSwitch = defineComponent({
  name: 'TSwitch',
  props: {
    label: String as PropType<TSwitchProps['label']>,
    disabled: Boolean as PropType<TSwitchProps['disabled']>,
    value: Boolean as PropType<TSwitchProps['value']>,
    modelValue: Boolean as PropType<TSwitchProps['modelValue']>,
    labelPosition: String as PropType<TSwitchProps['labelPosition']>,
    id: String as PropType<TSwitchProps['id']>,
  },
  emits: ['change', 'input', 'update:modelValue'],
  setup(_props, { attrs, emit }) {
    const props = computed(() => merge({}, defaultProps, _props, attrs));

    const checked = ref(props.value.modelValue || props.value.value);
    watch([() => props.value.modelValue, () => props.value.value], () => {
      checked.value = props.value.modelValue || props.value.value;
    });

    const handleInput = () => {
      emit('update:modelValue', !checked.value);
      emit('change', !checked.value);
      emit('input', !checked.value);
      checked.value = !checked.value;
    };
    const handleClick = (event: Event) => {
      if (props.value.disabled) {
        event?.preventDefault();
      } else {
        handleInput();
      }
    };
    const handleKeydown = (event: KeyboardEvent) => {
      const { key } = event;
      if (props.value.disabled || (key !== 'Enter' && key !== ' ')) {
        return;
      }
      handleInput();
    };

    const baseClass = useStyles();
    const containerClasses = computed(() => [
      baseClass.value,
      `${baseClass.value}--label-${props.value.labelPosition}`,
      {
        [`${baseClass.value}--checked`]: checked.value,
        [`${baseClass.value}--disabled`]: props.value.disabled,
      },
    ]);

    return () => (
      <div
        class={containerClasses.value}
        role="switch"
        aria-checked={checked.value}
        aria-disabled={props.value.disabled}
        aria-labelledby={props.value.id}
        onClick={handleClick}
        onKeydown={handleKeydown}
        tabindex={props.value.disabled ? undefined : 0}
      >
        <div class={`${baseClass.value}__switch`} />
        {props.value.label && (
          <label class={`${baseClass.value}__label`} id={props.value.id}>
            {props.value.label}
          </label>
        )}
      </div>
    );
  },
});
